import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Form, Result, message } from 'antd';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { UserOutlined } from '@ant-design/icons';
import { useThemeLabel } from 'client/components/schema/config/ClientConfigModule';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { CheckboxInputItem } from 'client/ui/form/input/CheckboxInputItem';
import { FormDividerSpace } from 'client/ui/space/FormDividerSpace';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import * as CitizenDto from 'common/dto/CitizenDto';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { logger } from 'client/core/logger/logger';
import qs from 'qs';
import { SecondaryText } from 'client/ui/display/text/SecondaryText';
import { ICitizenMailDto } from 'common/dto/CitizenDto';
import { Trans, useTranslation } from 'react-i18next';
import { resolvePublicPath } from 'client/core/appConfig';

interface Params {}

export interface UserEmailValidationPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina per la registrazione della mail di un utente autenticato con CIE
 */
export function UserEmailValidationPage(props: UserEmailValidationPageProps) {
  const privacyUrl = useThemeLabel('privacyUrl');
  const { t } = useTranslation();

  const urlQuery = qs.parse(props.location.search, {
    ignoreQueryPrefix: true
  });

  const token = urlQuery.token as string | undefined;
  const fullName = urlQuery.fullName as string | undefined;

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.registration.breadcrumb', 'Registrazione Utente')
  });

  const [register, registerState] = useApiMutation(CitizenApi.addEmail, {});

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };

  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };

  return (
    <FrontLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {/* Messaggio al successo dell'invio che sostituisce il Form */}
          {registerState.response ? (
            <Result
              status="success"
              title={t(
                'user.registration.response.success.title',
                'Registrazione effettuata con successo!'
              )}
              subTitle={t(
                'user.registration.response.success.sub-title-generic',
                'Per attivare l’utente segui le istruzioni che ti abbiamo inviato via email.'
              )}
            />
          ) : (
            <Formik<ICitizenMailDto>
              enableReinitialize
              initialValues={{ token: token! } as ICitizenMailDto}
              validate={schemaToValidator(CitizenDto.citizenMailValidator)}
              validateOnChange={false}
              onSubmit={async rawValues => {
                try {
                  const values = await CitizenDto.citizenMailValidator.validate(
                    rawValues,
                    { abortEarly: false }
                  );
                  await register({
                    data: { input: values }
                  });
                  message.success(
                    t(
                      'user.registration.response.success.title',
                      'Registrazione effettuata con successo!'
                    )
                  );
                } catch (e) {
                  logger.error('Non è stato possibile registarsi.', e);
                  message.error(
                    t(
                      'user.registration.response.error.message',
                      'Si è verificato un errore durante la registrazione'
                    )
                  );
                }
              }}
            >
              {({ isSubmitting, dirty }) => (
                <>
                  <PreventNavigation
                    when={dirty && !isSubmitting}
                    message={t(
                      'user.registration.prevent-navigation-message',
                      `Ci sono dati non salvati, abbandonare la pagina? (Tutte le modifiche non salvate andranno perse)`
                    )}
                  />
                  <FormikForm {...layout}>
                    <SecondaryText>
                      <Trans
                        t={t}
                        i18nKey="user.registration.email-validation.message"
                      >
                        Ciao <strong>{{ fullName }}</strong>, l’autenticazione è
                        andata a buon fine. Inserisci la tua eMail per
                        completare il profilo e procedere con l’accesso ai
                        servizi online.
                      </Trans>
                    </SecondaryText>
                    {/* Form Utente */}
                    <FormDividerSpace
                      title={t(
                        'user.registration.form.top-title',
                        'Dati di accesso'
                      )}
                      layout={layout}
                      style={{ marginTop: 0 }}
                    />
                    <TextInputItem
                      prefix={<UserOutlined style={{ color: '#d9d9d9' }} />}
                      name="email"
                      label="Email"
                    />

                    <CheckboxInputItem
                      name="privacyAcceptance"
                      label={t(
                        'user.registration.form.privacy-acceptance',
                        'Consenso'
                      )}
                      title={
                        <span>
                          {t(
                            'user.registration.form.privacy-acceptance-text',
                            'Acconsento al'
                          )}{' '}
                          <a
                            href={resolvePublicPath(
                              t(
                                `mixed:user.registration.form.privacy-cookie-policy-file-name`,
                                'privacy_e_cookie_policy.pdf'
                              )
                            )}
                            target="_blank"
                          >
                            {t(
                              'user.registration.form.privacy-acceptance-link-text',
                              'trattamento dei dati.'
                            )}
                          </a>
                        </span>
                      }
                    />

                    <Form.Item {...tailLayout}>
                      <Button
                        loading={isSubmitting}
                        htmlType="submit"
                        type="primary"
                        style={{ width: '100%' }}
                      >
                        {t(
                          'user.registration.form.submit-button',
                          'Registrati'
                        )}
                      </Button>
                    </Form.Item>
                  </FormikForm>
                </>
              )}
            </Formik>
          )}
        </Card>
      </div>
    </FrontLayout>
  );
}
